/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteAskingTeamMember,
  deleteGivingTeamMember,
  editAskingTeamMember,
  editGivingTeamMember,
  editInstruction,
  fetchInstructionById,
} from "services/doubtfulService";
import {
  DoubtfulCase,
  DoubtfulCaseStatus,
  FILE_URL,
  Roles,
  TeamMemberAsking,
  TeamMemberGiving,
} from "types";
import { BASE_URL } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";
import AskingTeamDecisionForm from "./fragment/AskingTeamDecisionForm";
import AskingTeamMemberForm from "./fragment/AskingTeamMemberForm";
import DoubtfulFeedbackSection from "./fragment/DoubtfulFeedback";
import GivingTeamDecisionForm from "./fragment/GivingTeamDecisionForm";
import GivingTeamMemberForm from "./fragment/GivingTeamMemberForm";
import RegistrarForm from "./fragment/RegistrarForm";
import DocumentViewerModal from "components/DocumentViewer";
import OrgUnitForm from "./fragment/OrgUnitForm";

type Props = {
  selectedId: string | number;
};

const DoubtfulDetail: React.FC<Props> = ({ selectedId }) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [instruction, setInstruction] = useState<DoubtfulCase | null>(null);
  const [openAddAskingTeam, setOpenAddAskingTeam] = useState(false);
  const [openAddGivingTeam, setOpenAddGivingTeam] = useState(false);
  const [openAddAskingTeamDecision, setOpenAskingTeamDecision] =
    useState(false);
  const [openAddGivingTeamDecision, setOpenGivingTeamDecision] =
    useState(false);
  const [openRegistrarForm, setOpenRegistrarForm] = useState(false);
  const [askingFormAction, setAskingFormAction] = useState<"Add" | "Edit">(
    "Add",
  );
  const [givingFormAction, setGivingFormAction] = useState<"Add" | "Edit">(
    "Add",
  );
  const [askingTeamMembers, setAskingTeamMembers] = useState<
    TeamMemberAsking[]
  >([]);
  const [givingTeamMembers, setGivingTeamMembers] = useState<
    TeamMemberGiving[]
  >([]);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const getStatusLabel = (status?: string | null) => {
    if (!status) return t("na");

    const normalizedStatus = status.toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const mappedStatusLabel =
      status in DoubtfulCaseStatus
        ? DoubtfulCaseStatus[status as keyof typeof DoubtfulCaseStatus]
        : undefined;

    const fallback =
      mappedStatusLabel ||
      status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    return t(`status_${normalizedStatus}`, { defaultValue: fallback });
  };

  const fetchData = () => {
    if (!selectedId) return;
    fetchInstructionById(selectedId)
      .then((data: DoubtfulCase) => {
        setInstruction(data);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_case_details"),
          "error",
        );
      });
  };

  useEffect(() => {
    fetchData();
  }, [selectedId, showTooltip]);

  useEffect(() => {
    if (instruction?.asking_team_members) {
      setAskingTeamMembers(instruction.asking_team_members);
    }
    if (instruction?.giving_team_members) {
      setGivingTeamMembers(instruction.giving_team_members);
    }
  }, [instruction]);

  const handleMakeAskingTeamLeader = (member: TeamMemberAsking) => {
    const oldTeamLeader = instruction?.asking_team_members?.find(
      (t) => t?.is_team_leader,
    );
    editAskingTeamMember({
      id: member?.id,
      is_team_leader: true,
    })
      .then(() => {
        if (oldTeamLeader) {
          editAskingTeamMember({
            id: oldTeamLeader?.id,
            is_team_leader: false,
          })?.then(() => {
            showTooltip(t("successfully_updated"), "success");
            fetchData();
          });
        } else {
          showTooltip(t("successfully_updated"), "success");
          fetchData();
        }
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  const handleMakeGivingTeamLeader = (member: TeamMemberAsking) => {
    const oldTeamLeader = instruction?.giving_team_members?.find(
      (t) => t?.is_team_leader,
    );
    editGivingTeamMember({
      id: member?.id,
      is_team_leader: true,
    })
      .then(() => {
        if (oldTeamLeader) {
          editGivingTeamMember({
            id: oldTeamLeader?.id,
            is_team_leader: false,
          })?.then(() => {
            showTooltip(t("successfully_updated"), "success");
            fetchData();
          });
        } else {
          showTooltip(t("successfully_updated"), "success");
          fetchData();
        }
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  const handleRemoveAskingMember = (id: string | number) => {
    deleteAskingTeamMember(id)
      .then(() => {
        showTooltip(t("successfully_deleted"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  const handleGivingAskingMember = (id: string | number) => {
    deleteGivingTeamMember(id)
      .then(() => {
        showTooltip(t("successfully_deleted"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  const updateStatus = (status: string) => {
    if (!selectedId) return;
    editInstruction({
      id: selectedId,
      request_status: status,
    })
      .then(() => {
        showTooltip(t("successfully_updated"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">❓</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("doubtful_detail_title")}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("doubtful_detail_subtitle")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap gap-3">
          {isProsecutor &&
            (instruction?.request_status === "Pending" ||
              instruction?.request_status === "respond_backto_prosecutor") && (
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                onClick={() => {
                  updateStatus("forwarded_to_cordinator");
                }}
              >
                {t("forward_to_coordinator")}
              </button>
            )}
          {isCoordinator &&
            instruction?.request_status === "forwarded_to_cordinator" &&
            !instruction?.team_resolution_summary_date &&
            !instruction?.team_draft_date && (
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 text-sm font-medium hover:bg-gray-300 transition-colors"
                onClick={() => {
                  updateStatus("respond_backto_prosecutor");
                }}
              >
                {t("respond_back")}
              </button>
            )}
          {isCoordinator &&
            instruction?.request_status === "forwarded_to_cordinator" &&
            !instruction?.team_resolution_summary_date &&
            !instruction?.team_draft_date && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
                  onClick={() => {
                    setOpenAddAskingTeam(true);
                    setAskingFormAction("Add");
                  }}
                >
                  {t("doubtful_detail_add_receiving_team")}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    if (
                      instruction?.asking_team_members &&
                      instruction?.asking_team_members?.filter(
                        (t) => t?.is_team_leader,
                      )?.length === 0
                    ) {
                      showTooltip(
                        t("doubtful_detail_team_leader_required"),
                        "error",
                      );
                      return;
                    }
                    updateStatus("assigned_to_team_asking");
                  }}
                  disabled={
                    instruction?.asking_team_members &&
                    instruction?.asking_team_members?.length === 0
                  }
                >
                  {t("doubtful_detail_forward_to_asking_team")}
                </button>
              </div>
            )}
          {isProsecutor &&
            loginData?.user_id ===
              askingTeamMembers?.find((t) => t?.is_team_leader)
                ?.admin_unit_id &&
            !instruction?.team_final_instruction_date &&
            (instruction?.request_status === "assigned_to_team_asking" ||
              instruction?.request_status === "Rejected") && (
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                onClick={() => {
                  setOpenAskingTeamDecision(true);
                }}
              >
                {instruction?.request_status === "Rejected"
                  ? t("update")
                  : t("add")}{" "}
                {t("decision")}
              </button>
            )}
          {isCoordinator &&
            instruction?.request_status === "forwarded_to_cordinator" &&
            (instruction?.team_resolution_summary_date ||
              instruction?.team_final_instruction_date ||
              instruction?.team_draft_date) && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors"
                  onClick={() => {
                    updateStatus(
                      instruction?.team_final_instruction_date
                        ? "forwarded_to_registrar_aftergiving"
                        : instruction.team_draft_date
                          ? "Approved"
                          : "forwarded_to_registrar",
                    );
                  }}
                >
                  {t("approve")}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                  onClick={() => {
                    updateStatus("Rejected");
                  }}
                >
                  {t("rejected")}
                </button>
              </div>
            )}
          {isCoordinator &&
            instruction?.request_status === "forwarded_to_cordinator_giving" &&
            loginData?.admin_unit === instruction.admin_unit_id && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
                  onClick={() => {
                    setOpenAddGivingTeam(true);
                    setGivingFormAction("Add");
                  }}
                >
                  {t("doubtful_detail_add_giving_team")}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    updateStatus("assigned_to_team_giving");
                  }}
                  disabled={
                    instruction?.giving_team_members &&
                    instruction?.giving_team_members?.length === 0
                  }
                >
                  {t("doubtful_detail_forward_to_giving_team")}
                </button>
              </div>
            )}
          {isProsecutor &&
            (instruction?.request_status === "assigned_to_team_giving" ||
              (instruction?.request_status === "Rejected" &&
                instruction?.team_final_instruction_date)) &&
            loginData?.admin_unit === instruction.admin_unit_id && (
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                onClick={() => {
                  setOpenGivingTeamDecision(true);
                }}
              >
                {instruction?.request_status === "Rejected"
                  ? t("update")
                  : t("add")}{" "}
                {t("decision")}
              </button>
            )}
          {isRegistrar &&
            (instruction?.request_status === "forwarded_to_registrar" ||
              instruction?.request_status ===
                "forwarded_to_registrar_aftergiving") &&
            loginData?.admin_unit === instruction.admin_unit_id && (
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                onClick={() => {
                  setOpenRegistrarForm(true);
                }}
              >
                {t("doubtful_detail_resolved")}
              </button>
            )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {!instruction ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">
                {t("doubtful_detail_loading_instruction_details")}
              </span>
            </div>
          ) : (
            <>
              {/* Case Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {t("crime_registration_number")}
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {instruction.crime_registration_number || t("na")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {t("incident_registration_number")}
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {instruction.incident_registration_number || t("na")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {t("prosecutor_registration_number")}
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {instruction.prosecutor_registration_number || t("na")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {t("request_status")}
                    </p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-1 ${
                        instruction.request_status &&
                        getStatusColor(instruction.request_status)
                      }`}
                    >
                      {getStatusLabel(instruction.request_status)}
                    </span>
                  </div>
                  {instruction?.registrar_refernece_out && (
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        {t("doubtful_detail_reference_out")}
                      </p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {instruction?.registrar_refernece_out}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {t("requested_date")}
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {instruction.requested_date
                        ? new Date(
                            instruction.requested_date,
                          ).toLocaleDateString()
                        : t("na")}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-600">
                      {t("instruction_description")}
                    </p>
                    <p className="text-sm text-gray-700 mt-2 bg-gray-50 p-4 rounded-lg">
                      {instruction.instruction_description || t("na")}
                    </p>
                    <DocumentViewerModal
                      documents={
                        (instruction?.documents?.filter(
                          (d) => d?.document_type === "instruction_doc",
                        ) || []) as any
                      }
                      baseUrl={`${BASE_URL}${FILE_URL.DOUBTFUL_CASE}`}
                      layout="list"
                      // label="Letter Upload"
                      hideEmptyFile={true}
                    />
                  </div>
                </div>
              </div>

              {/* Asking Team Members */}
              {askingTeamMembers.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t("doubtful_detail_asking_team_members")}
                  </h3>
                  <div className="space-y-3">
                    {askingTeamMembers.map((member, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-sm">👤</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {member.name}
                            </p>
                            {member.is_team_leader && (
                              <span className="text-xs font-semibold text-green-600">
                                {t("doubtful_detail_team_leader")}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isCoordinator &&
                            instruction?.request_status ===
                              "forwarded_to_cordinator" && (
                              <>
                                <button
                                  className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() =>
                                    handleMakeAskingTeamLeader(member)
                                  }
                                  disabled={member.is_team_leader}
                                >
                                  {t("doubtful_detail_make_leader")}
                                </button>
                                <button
                                  className="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded hover:bg-red-200"
                                  onClick={() =>
                                    handleRemoveAskingMember(member?.id || "")
                                  }
                                >
                                  {t("doubtful_detail_remove")}
                                </button>
                              </>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {(instruction?.team_draft_date ||
                    instruction?.team_resolution_summary_date) && (
                    <div className="mt-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        {t("decision")}
                      </h4>
                      <div className="space-y-4">
                        {instruction?.team_draft_date && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h5 className="font-semibold text-blue-800 mb-2">
                              {t(
                                "doubtful_detail_forwarded_to_higher_officials",
                              )}
                            </h5>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-gray-700">
                                  {t("doubtful_detail_draft_date")}:
                                </span>
                                <span className="text-gray-900">
                                  {new Date(
                                    instruction.team_draft_date,
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-gray-700">
                                  {t("doubtful_detail_draft_summary")}:
                                </span>
                                <span className="text-gray-900">
                                  {instruction.team_draft_summary || t("na")}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {instruction?.team_resolution_summary_date && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h5 className="font-semibold text-green-800 mb-2">
                              {t("doubtful_detail_resolved_by_ours")}
                            </h5>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-gray-700">
                                  {t("doubtful_detail_summary_date")}:
                                </span>
                                <span className="text-gray-900">
                                  {new Date(
                                    instruction.team_resolution_summary_date,
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-gray-700">
                                  {t("summary")}:
                                </span>
                                <span className="text-gray-900">
                                  {instruction.team_resolution_summary ||
                                    t("na")}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        <DocumentViewerModal
                          documents={
                            (instruction?.documents?.filter(
                              (d) => d?.document_type === "team_resolution_doc",
                            ) || []) as any
                          }
                          baseUrl={`${BASE_URL}${FILE_URL.DOUBTFUL_CASE}`}
                          layout="list"
                          // label="Letter Upload"
                          hideEmptyFile={true}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Giving Team Members */}
              {givingTeamMembers.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t("doubtful_detail_giving_team_members")}
                  </h3>
                  <div className="space-y-3">
                    {givingTeamMembers.map((member, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-sm">👤</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {member.name}
                            </p>
                            {member.is_team_leader && (
                              <span className="text-xs font-semibold text-green-600">
                                {t("doubtful_detail_team_leader")}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isCoordinator &&
                            instruction?.request_status ===
                              "forwarded_to_cordinator_giving" &&
                            !instruction?.team_final_instruction_summary && (
                              <>
                                <button
                                  className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() =>
                                    handleMakeGivingTeamLeader(member)
                                  }
                                  disabled={member.is_team_leader}
                                >
                                  {t("doubtful_detail_make_leader")}
                                </button>
                                <button
                                  className="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded hover:bg-red-200"
                                  onClick={() =>
                                    handleGivingAskingMember(member?.id || "")
                                  }
                                >
                                  {t("doubtful_detail_remove")}
                                </button>
                              </>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {instruction?.team_final_instruction_date && (
                    <div className="mt-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        {t("decision")}
                      </h4>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-gray-700">
                              {t("doubtful_detail_summary_date")}:
                            </span>
                            <span className="text-gray-900">
                              {new Date(
                                instruction.team_final_instruction_date,
                              ).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-gray-700">
                              {t("summary")}:
                            </span>
                            <span className="text-gray-900">
                              {instruction.team_final_instruction_summary ||
                                t("na")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <DocumentViewerModal
                    documents={
                      (instruction?.documents?.filter(
                        (d) =>
                          d?.document_type === "team_final_instruction_doc",
                      ) || []) as any
                    }
                    baseUrl={`${BASE_URL}${FILE_URL.DOUBTFUL_CASE}`}
                    layout="list"
                    // label="Letter Upload"
                    hideEmptyFile={true}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {loginData?.admin_unit === instruction?.admin_unit_id &&
            isRegistrar &&
            instruction?.org_id === "" && (
              <OrgUnitForm
                instructuion={instruction}
                onClose={() => fetchData()}
              />
            )}
          {instruction && (
            <DoubtfulFeedbackSection
              instruction={instruction}
              instruction_feedbacks={instruction?.instruction_feedbacks || []}
              setRefetch={() => fetchData()}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {instruction && openAddAskingTeam && (
        <AskingTeamMemberForm
          action={askingFormAction}
          instruction={instruction}
          onClose={() => {
            setOpenAddAskingTeam(false);
          }}
        />
      )}
      {instruction && openAddAskingTeamDecision && (
        <AskingTeamDecisionForm
          instruction={instruction}
          onClose={() => {
            setOpenAskingTeamDecision(false);
          }}
        />
      )}
      {instruction && openAddGivingTeam && (
        <GivingTeamMemberForm
          action={givingFormAction}
          instruction={instruction}
          onClose={() => {
            setOpenAddGivingTeam(false);
          }}
        />
      )}
      {instruction && openAddGivingTeamDecision && (
        <GivingTeamDecisionForm
          instruction={instruction}
          onClose={() => {
            setOpenGivingTeamDecision(false);
          }}
        />
      )}
      {instruction && openRegistrarForm && (
        <RegistrarForm
          instruction={instruction}
          onClose={() => {
            setOpenRegistrarForm(false);
          }}
        />
      )}
    </div>
  );
};

// Helper function for status colors
const getStatusColor = (status?: string) => {
  if (!status) return "bg-gray-100 text-gray-800";

  switch (status.toLowerCase()) {
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "approved":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    case "forwarded_to_cordinator":
      return "bg-blue-100 text-blue-800";
    case "assigned_to_team_asking":
      return "bg-indigo-100 text-indigo-800";
    case "forwarded_to_registrar":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export default DoubtfulDetail;
